import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { ChevronLeft, Crown, Gift, History, ShieldCheck, Trophy, WalletCards, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import {
  claimVipReward,
  fetchVipRewardsStatus,
  resolveVipRewardImageUrl,
  vipRewardsCacheScopeForUser,
} from '../../services/vipRewards';

import VipRewardsHistory from './components/VipRewardsHistory';
import VipPageHeader from './components/VipPageHeader';
import './VipRewardsPage.css';

const FALLBACK_BANNER = '/assets/fifa-kit-home.png';

const formatFp = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount) || amount <= 0) return '0 FP';
  return `${Number.isInteger(amount) ? amount : amount.toFixed(2).replace(/\.?0+$/, '')} FP`;
};

const formatMoney = (value) => `$${Number(value || 0).toFixed(2)}`;

const formatCountdown = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${String(minutes).padStart(2, '0')}m ${String(seconds % 60).padStart(2, '0')}s`;
};

const claimAmount = (claim) => {
  if (claim?.type === 'cashback') return Number(claim.reward_cash ?? claim.reward_fp ?? 0);
  return Number(claim?.reward_fp || 0);
};

const claimLabel = (claim) => {
  const amount = claimAmount(claim);
  if (claim?.type === 'cashback') return `${formatMoney(amount)} wallet cashback`;
  return formatFp(amount);
};

export default function VipRewardsPage() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const historyOpen = searchParams.get('view') === 'history';
  const historyTrigger = useRef(null);
  const pageScroll = useRef(0);
  const wasHistoryOpen = useRef(false);
  const setHistory = (open) => {
    if (open) pageScroll.current = window.scrollY;
    const next = new URLSearchParams(searchParams);
    if (open) next.set('view', 'history'); else next.delete('view');
    setSearchParams(next);
  };
  useEffect(() => {
    if (historyOpen || wasHistoryOpen.current) window.scrollTo(0, historyOpen ? 0 : pageScroll.current);
    if (!historyOpen && wasHistoryOpen.current && historyTrigger.current) historyTrigger.current.focus({ preventScroll: true });
    wasHistoryOpen.current = historyOpen;
  }, [historyOpen]);
  const explorePath = tenantSlug ? `/${tenantSlug}/app/explore` : '/app/explore';
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState('');
  const [cooldownEndsAt, setCooldownEndsAt] = useState(0);
  const [clockNow, setClockNow] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const requestVersion = useRef(0);
  const claimInFlight = useRef(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const item = status?.item || {};
  const availableClaims = (status?.available_claims || []).filter(claim => claim.id && claim.status === 'available');
  const progress = status?.cashback_progress || {};
  const tiers = item?.freeplay_offer?.tiers || [];
  const cashback = item?.cashback || {};
  const freeplayEnabled = item?.freeplay_offer?.enabled !== false;
  const cashbackEnabled = item?.cashback?.enabled !== false;
  // An uploaded flyer takes priority. The live cashback summary is only a fallback;
  // do not replace a tenant's artwork just because cashback is enabled.
  const bannerUrl = resolveVipRewardImageUrl(
    item.banner_image_url,
    cashbackEnabled ? '' : item.fallback_banner_url || FALLBACK_BANNER,
  );
  const cacheScope = vipRewardsCacheScopeForUser(user);
  const { tenantId, userId } = cacheScope;
  const requiredDeposits = Number(cashback.required_deposits ?? 6);
  const currentDeposits = Math.min(requiredDeposits, Number(progress.deposit_count || 0));
  const cooldownSeconds = Math.max(0, Math.ceil((cooldownEndsAt - clockNow) / 1000));
  const waitingForCashback = cashbackEnabled && cooldownSeconds > 0;
  const claimActionsBlocked = Boolean(claiming) || refreshing || refreshFailed;
  // Match a persisted offer to its exact tier snapshot, never infer eligibility
  // from a displayed amount or the user's wallet balance.
  const tierClaims = tiers.map(tier => availableClaims.filter(claim => {
    const saved = claim.meta?.tier;
    return claim.type === 'freeplay_offer' && saved
      && Number(saved.min) === Number(tier.min)
      && (saved.max == null ? tier.max == null : tier.max != null && Number(saved.max) === Number(tier.max))
      && Number(claim.reward_fp) === Number(tier.reward_fp);
  }));
  const assignedIds = new Set(tierClaims.flat().map(claim => claim.id));
  const otherFreeplayClaims = availableClaims.filter(claim => claim.type === 'freeplay_offer' && !assignedIds.has(claim.id));
  const cashbackClaims = availableClaims.filter(claim => claim.type === 'cashback');
  const cashbackBlocked = claimActionsBlocked || waitingForCashback || currentDeposits < requiredDeposits;

  const acceptStatus = useCallback((data) => {
    setStatus(data);
    // Use server-computed remaining time; a wrong phone clock cannot skip the wait.
    const seconds = Number(data?.cashback_cooldown?.remaining_seconds || 0);
    const now = performance.now();
    setClockNow(now);
    setCooldownEndsAt(data?.cashback_cooldown?.active && Number.isFinite(seconds) && seconds > 0
      ? now + seconds * 1000 : 0);
  }, []);

  const load = useCallback(async (silent = false) => {
    const version = ++requestVersion.current;
    if (!silent) setLoading(true);
    setRefreshing(silent);
    setRefreshFailed(false);
    if (!silent) setLoadFailed(false);
    try {
      const data = await fetchVipRewardsStatus({ tenantId, userId, preferCache: false });
      if (version === requestVersion.current) acceptStatus(data);
    } catch (error) {
      if (version !== requestVersion.current) return;
      setRefreshFailed(silent);
      setLoadFailed(!silent);
      toast.error(error?.response?.data?.detail || 'Could not load VIP Rewards');
    } finally {
      if (version === requestVersion.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [tenantId, userId, acceptStatus]);

  useEffect(() => {
    setStatus(null);
    setCooldownEndsAt(0);
    setClaiming('');
    claimInFlight.current = false;
    load();
    return () => { requestVersion.current += 1; };
  }, [load]);

  useEffect(() => {
    if (!cooldownEndsAt || claiming) return undefined;
    const timer = window.setInterval(() => {
      const now = performance.now();
      setClockNow(now);
      if (now >= cooldownEndsAt) {
        window.clearInterval(timer);
        setCooldownEndsAt(0);
        // Refresh once at expiry. The server, not the timer, makes a claim available.
        load(true);
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldownEndsAt, load, claiming]);

  const handleClaim = async (claimId) => {
    if (!claimId || claimInFlight.current) return;
    claimInFlight.current = true;
    const version = ++requestVersion.current;
    setClaiming(claimId);
    setRefreshing(false);
    setRefreshFailed(false);
    try {
      const data = await claimVipReward(claimId, cacheScope);
      if (version !== requestVersion.current) return;
      toast.success(`Claimed ${data.reward_label || 'VIP reward'}`);
      const nextStatus = data.status || await fetchVipRewardsStatus(cacheScope);
      if (version === requestVersion.current) acceptStatus(nextStatus);
    } catch (error) {
      if (version === requestVersion.current) toast.error(error?.response?.data?.detail || 'Claim failed');
    } finally {
      if (version === requestVersion.current) { setClaiming(''); claimInFlight.current = false; }
    }
  };

  if (historyOpen) return <VipRewardsHistory key={JSON.stringify([tenantId, userId])} onBack={() => setHistory(false)} />;

  return (
    <div className="player-app vip-page min-h-screen text-white" data-testid="vip-rewards-page">
      <VipPageHeader>
        <div className="vip-header-inner">
          <button type="button" onClick={() => navigate(explorePath)} className="vip-back" aria-label="Back" data-testid="vip-rewards-back"><ChevronLeft size={22} /></button>
          <span className="vip-emblem" aria-hidden="true"><Crown size={25} strokeWidth={1.8} /></span>
          <div className="vip-heading"><h1>{item.title || 'VIP Rewards'}</h1><p>{item.subtitle || 'Load bonuses & cashback'}</p></div>
          <button type="button" ref={historyTrigger} onClick={() => setHistory(true)} className="vip-history-button" aria-label="Reward history"><History size={19} /><span>History</span></button>
        </div>
      </VipPageHeader>

      <div className="vip-main">
        {loading ? (
          <VipLoading />
        ) : loadFailed ? (
          <div className="vip-empty" role="alert"><ShieldCheck size={28} /><h2>Rewards could not load</h2><p>Your rewards have not changed. Check your connection and try again.</p><button type="button" className="vip-secondary" onClick={() => load()}>Try again</button></div>
        ) : !status?.enabled ? (
          <div className="vip-empty">
            <ShieldCheck className="mx-auto mb-3 h-10 w-10 text-white/45" />
            <p className="display text-lg font-extrabold">VIP Rewards are not active here.</p>
          </div>
        ) : (
          <>
            <div className="vip-banner">
              <VipRewardsBanner
                key={JSON.stringify([tenantId, bannerUrl])}
                src={bannerUrl}
                title={item.title || 'VIP Rewards'}
                cashbackEnabled={cashbackEnabled}
                requiredDeposits={requiredDeposits}
                cashbackPercent={Number(cashback.cashback_percent ?? 5)}
              />
            </div>

            <div className="vip-offers">
              {(freeplayEnabled || otherFreeplayClaims.length > 0) && <section className="vip-offer" aria-labelledby="vip-freeplay-title">
                <div className="vip-offer-heading"><Gift size={21} aria-hidden="true" /><h2 id="vip-freeplay-title">Freeplay Load Bonus</h2></div>
                <p className="vip-offer-description">Load a game from your Main Wallet. Claim once every {Number(item.freeplay_offer?.cooldown_hours || 24)} hours.</p>
                <ul className="vip-tiers" aria-label="Freeplay load bonuses">
                  {tiers.map((tier, index) => <li key={`${tier.min}-${tier.max}-${index}`}>
                    <div className="vip-tier-copy"><span className="vip-tier-range">{formatMoney(tier.min)}{tier.max != null ? ` – ${formatMoney(tier.max)}` : '+'}</span></div>
                    <div className="vip-row-actions">
                      {(tierClaims[index].length ? tierClaims[index] : [null]).map((claim, position) => <ClaimAction key={claim?.id || 'unavailable'} claim={claim} disabled={claimActionsBlocked} busy={claiming === claim?.id} onClaim={handleClaim} label={`Claim ${formatFp(tier.reward_fp)}`} rewardLabel={formatFp(tier.reward_fp)} fallbackId={`tier-${index}-${position}`} />)}
                    </div>
                  </li>)}
                  {otherFreeplayClaims.map(claim => <li key={claim.id}>
                    <div className="vip-tier-copy"><span className="vip-tier-range">{claim.title || 'Freeplay Load Bonus'}</span></div>
                    <ClaimAction claim={claim} disabled={claimActionsBlocked} busy={claiming === claim.id} onClaim={handleClaim} label={`Claim ${claimLabel(claim)}`} />
                  </li>)}
                </ul>
                {tiers.length === 0 && otherFreeplayClaims.length === 0 && <div className="vip-no-tiers"><p className="vip-muted">No load bonus tiers are configured yet.</p><ClaimAction disabled fallbackId="freeplay" label="Claim Freeplay bonus" /></div>}
                <p className="vip-footnote"><Clock size={14} aria-hidden="true" />Claim unlocks when a qualifying reward is available.</p>
              </section>}

              {cashbackEnabled && <section className="vip-offer vip-cashback" aria-labelledby="vip-cashback-title">
                <div className="vip-cashback-row">
                  <div className="vip-cashback-copy">
                    <div className="vip-offer-heading"><WalletCards size={21} aria-hidden="true" /><h2 id="vip-cashback-title">Cashback Streak</h2></div>
                    <p className="vip-cashback-rate">{Number(cashback.cashback_percent ?? 5)}% cashback · {currentDeposits}/{requiredDeposits} deposits</p>
                    <div className="vip-progress-track" role="progressbar" aria-label="Cashback deposit progress" aria-valuemin={0} aria-valuemax={requiredDeposits} aria-valuenow={currentDeposits}>
                      <div className="vip-progress-fill" style={{ width: `${Math.min(100, (currentDeposits / Math.max(1, requiredDeposits)) * 100)}%` }} />
                    </div>
                    {cashbackClaims.length > 0 && <p className="vip-cashback-amount">{cashbackClaims.map(claimLabel).join(' · ')}</p>}
                  </div>
                  <div className="vip-row-actions">{(cashbackClaims.length ? cashbackClaims : [null]).map(claim => <ClaimAction key={claim?.id || 'unavailable'} claim={claim} disabled={cashbackBlocked} busy={claiming === claim?.id} onClaim={handleClaim} label="Claim cashback" fallbackId="cashback" unavailableLabel={waitingForCashback ? 'Cooldown' : 'Unavailable'} />)}</div>
                </div>
                <details className="vip-rules"><summary>How your streak works</summary>
                  <p>Make {requiredDeposits} successful wallet deposits without a game redeem and claim {Number(cashback.cashback_percent ?? 5)}% cashback on those deposits.</p>
                  <p className="vip-qualifying-total">Qualifying wallet deposit total: {formatMoney(progress.deposit_total || 0)}</p>
                  <p className="vip-next-step">{currentDeposits < requiredDeposits
                    ? `${requiredDeposits - currentDeposits} more qualifying ${requiredDeposits - currentDeposits === 1 ? 'deposit' : 'deposits'} to complete your streak.`
                    : 'Deposit target reached. Claim availability is confirmed by the server.'}</p>
                  <p>Any successful game redeem resets the count and total to zero. Loading a game does not count as a deposit.</p>
                  <p>Claiming clears the deposits used for that cashback. Extra or new deposits count toward your next claim.</p>
                </details>
                  {waitingForCashback && (
                    <div className="vip-cooldown" data-testid="cashback-cooldown">
                      <p className="text-sm font-bold text-[#FDE68A]">
                        {currentDeposits >= requiredDeposits ? 'Next claim available in' : 'Cashback cooldown:'} {formatCountdown(cooldownSeconds)}
                      </p>
                      <p className="mt-1 text-xs text-white/70">
                        {currentDeposits >= requiredDeposits
                          ? 'Your deposit requirement is complete. The cooldown started when you last claimed cashback; a successful game redeem still resets this streak.'
                          : `The cooldown started when you last claimed cashback. Your next claim also requires ${requiredDeposits} new qualifying wallet deposits.`}
                      </p>
                    </div>
                  )}
                  {refreshing && <p className="mt-3 text-sm text-[#FDE68A]" role="status">Checking cashback eligibility…</p>}
                  {refreshFailed && (
                    <div className="mt-3 text-sm text-[#FDE68A]" role="status">
                      Could not refresh cashback eligibility.
                      <button type="button" onClick={() => load(true)} className="ml-2 underline">Retry</button>
                    </div>
                  )}

              </section>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function VipRewardsBanner({ src, title, cashbackEnabled, requiredDeposits, cashbackPercent }) {
  const [failed, setFailed] = useState(false);
  const showImage = src && !failed;
  return <div className={`relative grid min-h-[168px] w-full min-w-0 bg-white/5 ${showImage ? 'aspect-[1600/760]' : ''}`}>
    {showImage ? <img
      src={src}
      alt={`${title} flyer`}
      className="absolute inset-0 h-full w-full object-contain"
      draggable={false}
      onError={() => setFailed(true)}
      data-testid="vip-rewards-banner"
    /> : cashbackEnabled ? (
      <div className="flex min-w-0 flex-col items-center justify-center gap-2 p-5 text-center" data-testid="vip-rewards-banner-fallback">
        <Trophy className="h-9 w-9 text-[#FFD93D]" />
        <p className="display text-xl font-black text-[#FFD93D]">VIP Rewards</p>
        <p className="font-bold">{requiredDeposits} wallet deposits · {cashbackPercent}% cashback</p>
        <p className="text-xs text-white/65">Any successful game redeem resets your streak.</p>
      </div>
    ) : (
      <div className="grid h-full place-items-center text-sm font-bold text-white/45" data-testid="vip-rewards-banner-fallback">VIP Rewards</div>
    )}
  </div>;
}

function ClaimAction({ claim, disabled = false, busy = false, onClaim, label, rewardLabel, fallbackId, unavailableLabel = 'Unavailable' }) {
  const unavailable = !claim || disabled;
  const amountLabel = claim ? (claim.type === 'cashback' ? formatMoney(claimAmount(claim)) : formatFp(claimAmount(claim))) : rewardLabel;
  const buttonText = amountLabel ? `Claim ${amountLabel}` : 'Claim';
  const accessibleLabel = claim?.type === 'cashback' ? `${buttonText} cashback` : label;
  return <div className="vip-claim-action">
    <button type="button" className="vip-claim-button" disabled={unavailable} aria-label={busy ? 'Claiming…' : accessibleLabel} aria-busy={busy}
      data-testid={claim ? `vip-claim-${claim.id}` : `vip-claim-unavailable-${fallbackId}`}
      onClick={() => { if (!unavailable) onClaim(claim.id); }}>
      {busy ? 'Claiming…' : buttonText}
    </button>
    <span className={unavailable ? 'vip-claim-state' : 'vip-claim-state is-ready'}>{busy ? 'Please wait' : unavailable ? unavailableLabel : 'Ready'}</span>
  </div>;
}

function VipLoading() {
  return <div className="vip-loading" role="status" aria-label="Loading VIP Rewards">
    <div className="vip-skeleton vip-skeleton-banner" />
    {[1, 2].map(key => <div className="vip-skeleton-section" key={key}><div className="vip-skeleton vip-skeleton-title" /><div className="vip-skeleton vip-skeleton-row" /><div className="vip-skeleton vip-skeleton-row" /><div className="vip-skeleton vip-skeleton-row" /></div>)}
  </div>;
}
